import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import { useGetVideoWalls } from '../../queries/videoWall';
import {
  useGetLocations, useGetLocation, useCreateLocation, useUpdateLocation, useDeleteLocation,
  useSetLocationWalls, countryToTimezone,
} from '../../queries/locations';
import FeatureLocked from '../SharedComponents/FeatureLocked';
import TimezonePicker from '../../components/ui/TimezonePicker';
import { tzLabel } from '../../config/timezones';

/**
 * Embeddable Locations manager — the locations list + CRUD, reused by the standalone
 * page AND the Schedule page's "Locations" tab (cafe + restaurant). Each card shows
 * the location's country · timezone, the devices assigned to it (as chips), a device
 * count, and edit/delete. `showWalls` adds the wall-assignment tool (restaurant only).
 */
export default function LocationsManager({ showWalls = false }) {
  const { t } = useTranslation();
  const { data: locations = [], isLoading, error } = useGetLocations();
  const { data: walls = [] } = useGetVideoWalls();
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const del = useDeleteLocation();

  const EMPTY = { id: null, name: '', city: '', country: '', timezone: 'Asia/Riyadh' };
  const [editing, setEditing] = useState(null);
  const [tzAuto, setTzAuto] = useState(false);
  const [membersFor, setMembersFor] = useState(null);


  const openCreate = () => { setTzAuto(false); setEditing({ ...EMPTY }); };
  const openEdit = (l) => {
    setTzAuto(false);
    setEditing({ id: l.id, name: l.name || '', city: l.city || '', country: l.country || '', timezone: l.timezone || 'Asia/Riyadh' });
  };

  const onCountryChange = (country) => setEditing((f) => {
    const next = { ...f, country };
    const suggested = countryToTimezone(country);
    if (suggested && (tzAuto || f.timezone === 'Asia/Riyadh')) { next.timezone = suggested; setTzAuto(true); }
    return next;
  });

  const saveLocation = async () => {
    const f = editing;
    if (!f?.name.trim()) return;
    const payload = { name: f.name.trim(), city: f.city, country: f.country, timezone: f.timezone };
    if (f.id) await update.mutateAsync({ id: f.id, ...payload });
    else await create.mutateAsync(payload);
    setEditing(null);
  };

  const confirmDelete = (l) => confirmDialog({
    message: t('video_wall.locations.delete_confirm', { name: l.name }),
    header: t('video_wall.locations.delete_title'), icon: 'pi pi-exclamation-triangle',
    acceptClassName: 'p-button-danger', acceptLabel: t('video_wall.locations.delete'),
    rejectLabel: t('video_wall.locations.cancel'), accept: () => del.mutate(l.id),
  });

  if (error?.response?.status === 402) {
    return <FeatureLocked feature="locations" message={error.response.data?.message} />;
  }

  return (
    <>
      <ConfirmDialog />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.locations.subtitle')}</p>
        <Button label={t('devices.location_create', 'Add Location')} icon="pi pi-plus" onClick={openCreate} />
      </div>

      {isLoading ? (
        <div className="row g-3">{[...Array(3)].map((_, i) => <div key={i} className="col-12 col-md-6 col-lg-4"><Skeleton height="11rem" /></div>)}</div>
      ) : locations.length === 0 ? (
        <div className="text-center p-5" style={{ background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
          <div style={{ fontSize: '3rem' }}>📍</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.locations.empty_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.locations.empty_subtitle')}</p>
          <Button label={t('devices.location_create', 'Add Location')} icon="pi pi-plus" onClick={openCreate} />
        </div>
      ) : (
        <div className="row g-3">
          {locations.map((l) => {
            const devices = l.devices || [];
            return (
              <div key={l.id} className="col-12 col-md-6 col-lg-4">
                <div className="h-100 p-3 d-flex flex-column" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)', boxShadow: 'var(--pp-shadow-sm)' }}>
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                    <h5 className="mb-0 text-truncate" style={{ color: 'var(--pp-text-primary)' }}>{l.name}</h5>
                    <span style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)', fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>
                      <i className="fa-solid fa-desktop me-1" />{l.devices_count ?? devices.length}
                    </span>
                  </div>
                  <small style={{ color: 'var(--pp-text-secondary)' }}>{l.country}</small>
                  {/* The timezone is THE thing this record exists for — it's what
                      makes a schedule mean "12:00 here" rather than "12:00 at head
                      office". Shown as its own chip, not buried in a joined string. */}
                  <div className="mt-1">
                    <span style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                      <i className="fa-regular fa-clock me-1" />
                      {l.timezone ? tzLabel(l.timezone) : t('video_wall.locations.timezone_unset', 'Timezone not set')}
                    </span>
                  </div>

                  {/* Devices assigned to this location */}
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {devices.length === 0 ? (
                      <small style={{ color: 'var(--pp-text-tertiary, var(--pp-text-secondary))', fontStyle: 'italic' }}>
                        {t('devices.location_no_devices', 'No devices assigned')}
                      </small>
                    ) : devices.map((d) => (
                      <span key={d.id} style={{ background: 'var(--pp-primary-subtle, #eef2ff)', color: 'var(--pp-primary)', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                        <i className="fa-solid fa-desktop me-1" style={{ fontSize: 9 }} />{d.name || d.serial_name}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto d-flex align-items-center justify-content-end gap-1 pt-3">
                    {showWalls && <Button icon="pi pi-th-large" rounded text aria-label={t('video_wall.locations.manage_walls')} onClick={() => setMembersFor(l)} />}
                    <Button icon="pi pi-pencil" rounded text aria-label={t('video_wall.locations.edit', 'Edit')} onClick={() => openEdit(l)} />
                    <Button icon="pi pi-trash" rounded text severity="danger" aria-label={t('video_wall.locations.delete')} onClick={() => confirmDelete(l)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog visible={!!editing} onHide={() => setEditing(null)}
        header={editing?.id ? t('video_wall.locations.edit_title', 'Edit location') : t('devices.location_create', 'Add Location')}
        style={{ width: 'min(95vw, 480px)' }}
        footer={<div className="d-flex justify-content-end gap-2"><Button label={t('video_wall.locations.cancel')} text onClick={() => setEditing(null)} /><Button label={t('video_wall.locations.save')} icon="pi pi-check" loading={create.isLoading || update.isLoading} disabled={!editing?.name.trim()} onClick={saveLocation} /></div>}>
        {editing && (
          <div className="d-flex flex-column gap-3 pt-2">
            <div>
              <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.locations.name')}</label>
              <InputText className="w-100" value={editing.name} onChange={(e) => setEditing((f) => ({ ...f, name: e.target.value }))} placeholder={t('video_wall.locations.name_ph')} maxLength={150} autoFocus />
            </div>
            <div className="row g-2">
              <div className="col-6">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.locations.city')}</label>
                <InputText className="w-100" value={editing.city} onChange={(e) => setEditing((f) => ({ ...f, city: e.target.value }))} maxLength={120} />
              </div>
              <div className="col-6">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.locations.country')}</label>
                <InputText className="w-100" value={editing.country} onChange={(e) => onCountryChange(e.target.value)} maxLength={120} />
              </div>
            </div>
            <div>
              <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.locations.timezone')}</label>
              <TimezonePicker
                value={editing.timezone}
                onChange={(tz) => { setTzAuto(false); setEditing((f) => ({ ...f, timezone: tz })); }}
                allowUnset
              />
              <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)' }}>
                {tzAuto
                  ? t('video_wall.locations.timezone_auto', { tz: tzLabel(editing.timezone), defaultValue: 'Auto-set from country: {{tz}}' })
                  : t('video_wall.locations.timezone_hint')}
              </small>
              {/* The precedence that was invisible everywhere: a branch zone
                  beats the company setting for every device in this branch. */}
              <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)', fontStyle: 'italic' }}>
                {editing.timezone
                  ? t('video_wall.locations.timezone_overrides', 'This overrides the company timezone for devices in this branch.')
                  : t('video_wall.locations.timezone_inherits', 'Leave unset to use the company timezone.')}
              </small>
            </div>
          </div>
        )}
      </Dialog>

      {showWalls && <LocationWallsDialog location={membersFor} walls={walls} onHide={() => setMembersFor(null)} />}
    </>
  );
}

function LocationWallsDialog({ location, walls, onHide }) {
  const { t } = useTranslation();
  const { data: detail } = useGetLocation(location?.id, !!location);
  const setWalls = useSetLocationWalls();
  const [selected, setSelected] = useState([]);

  useEffect(() => { if (detail) setSelected((detail.walls || []).map((w) => w.id)); }, [detail]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => { await setWalls.mutateAsync({ id: location.id, wall_ids: selected }); onHide(); };

  return (
    <Dialog visible={!!location} onHide={onHide} header={location ? t('video_wall.locations.manage_walls_title', { name: location.name }) : ''} style={{ width: 'min(95vw, 520px)' }}
      footer={<div className="d-flex justify-content-end gap-2"><Button label={t('video_wall.locations.cancel')} text onClick={onHide} /><Button label={t('video_wall.locations.save')} icon="pi pi-check" loading={setWalls.isLoading} onClick={save} /></div>}>
      {walls.length === 0 ? (
        <p className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.locations.no_walls')}</p>
      ) : (
        <div className="d-flex flex-column gap-2">
          {walls.map((w) => {
            const otherLoc = w.location_id && w.location_id !== location?.id;
            return (
              <label key={w.id} className="d-flex align-items-center gap-2 p-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)', cursor: 'pointer' }}>
                <Checkbox checked={selected.includes(w.id)} onChange={() => toggle(w.id)} />
                <span style={{ color: 'var(--pp-text-primary)' }}>{w.name}</span>
                <small style={{ color: 'var(--pp-text-secondary)' }}>· {w.rows}×{w.columns}</small>
                {otherLoc && <small className="ms-auto" style={{ color: 'var(--pp-warning)' }}>{t('video_wall.locations.in_other_location')}</small>}
              </label>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
